import { difference, intersection } from 'lodash-es';
import { type JSX, ReactNode, useEffect, useState } from 'react';

import { Button } from 'Components/Buttons/Buttons';
import { IconButton } from 'Components/Buttons/IconButton';
import { FormFieldText } from 'Components/FormField/FormFieldText/FormFieldText';
import { Text } from 'Components/Text/Text';
import { ICON_CLOSE, ICON_MOVE_OR_SCROLL_LEFT, ICON_MOVE_OR_SCROLL_RIGHT } from 'Config/Icons';

import styles from './MultipleMappingBase.module.css';

export interface MappableItem {
    id: string;
    title: string;
    description: string;
}

/**
 * Renders a generic collection of items into two lists: a list of items on the left NOT mapped to a given entity, and a list of items on the right that ARE mapped to the entity.
 * For example, `MultipleControlMapping` makes use of this component to display controls that are and aren't mapped to issues, exceptions, etc.
 *
 * @param mappingItems - an array of all unmapped and mapped items
 * @param currentMappedIdentifiers - an array of the identifiers of all mapped items
 */
export interface MultipleMappingBaseProps<T extends MappableItem> {
    mappingItems: T[];
    handleChange: (items: string[]) => void;
    sortMappingItems: (mappingItems: T[]) => void;
    currentMappedIdentifiers?: string[];
    renderMappingItems: (mappingItems: T[], handleSelected: (value: T, e: React.MouseEvent<HTMLDivElement, MouseEvent>) => void, selected: T[]) => ReactNode;
}
export const MultipleMappingBase = <T extends MappableItem>(props: MultipleMappingBaseProps<T>): JSX.Element => {
    const [unmappedItems, setUnmappedItems] = useState<T[]>([]);
    const [mappedItems, setMappedItems] = useState<T[]>([]);
    const [selected, setSelected] = useState<T[]>([]);
    const [previousRow, setPreviousRow] = useState<T | undefined>();
    const [filterValue, setFilterValue] = useState<string>('');

    /** Build and sort the mapped and unmapped lists of items. */
    useEffect(() => {
        // items is filtered array of item not already mapped
        const filteredUnmappedItems = props.mappingItems.filter((item) => !props.currentMappedIdentifiers?.includes(item.id));
        props.sortMappingItems(filteredUnmappedItems);
        setUnmappedItems(filteredUnmappedItems);

        // items is filtered array of item already mapped
        const filteredMappedItems = props.mappingItems.filter((item) => props.currentMappedIdentifiers?.includes(item.id));
        props.sortMappingItems(filteredMappedItems);
        setMappedItems(filteredMappedItems);
    }, [props]);

    const handleSelected = (value: T, e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
        if (unmappedItems && mappedItems) {
            const currentIndex = selected.indexOf(value);
            let newChecked: T[] = [...selected];
            setPreviousRow(value);
            //e.shift selects a block
            if (e.shiftKey) {
                if (currentIndex === -1 && previousRow) {
                    const newSelection: T[] = [];
                    if (unmappedItems.indexOf(previousRow) > -1 && unmappedItems.indexOf(value) > -1) {
                        const prevIndex = unmappedItems.indexOf(previousRow);
                        const newIndex = unmappedItems.indexOf(value);
                        if (prevIndex < newIndex) {
                            for (let i = prevIndex; i <= newIndex; i++) {
                                newSelection.push(unmappedItems[i]);
                            }
                        } else {
                            for (let i = newIndex; i <= prevIndex; i++) {
                                newSelection.push(unmappedItems[i]);
                            }
                        }
                    } else if (mappedItems.indexOf(previousRow) > -1 && mappedItems.indexOf(value) > -1) {
                        const prevIndex = mappedItems.indexOf(previousRow);
                        const newIndex = mappedItems.indexOf(value);
                        if (prevIndex < newIndex) {
                            for (let i = prevIndex; i <= newIndex; i++) {
                                newSelection.push(mappedItems[i]);
                            }
                        } else {
                            for (let i = newIndex; i <= prevIndex; i++) {
                                newSelection.push(mappedItems[i]);
                            }
                        }
                    }
                    newChecked = newSelection;
                }
                //allows the user to select or deselect more than one item not in sequence
            } else if (e.ctrlKey || e.metaKey) {
                if (currentIndex === -1) {
                    newChecked.push(value);
                } else {
                    newChecked.splice(currentIndex, 1);
                }
                //if no keys are pressed, adds or subtracts the one item that was selected.
            } else {
                if (currentIndex === -1) {
                    newChecked = [value];
                }
            }

            setSelected(
                newChecked.filter((item) => {
                    return item.title.toLowerCase().includes(filterValue.toLowerCase()) || item.description.toLowerCase().includes(filterValue.toLowerCase());
                })
            );
        } else return;
    };

    const setFilter = (event: React.FormEvent<HTMLInputElement>): void => {
        event.preventDefault();
        setFilterValue(event.currentTarget.value);
    };
    const unmappedSelectedItems = intersection(selected, unmappedItems);
    const mappedSelectedItems = intersection(selected, mappedItems);

    const handleUnmappedToMapped = () => {
        if (unmappedItems && mappedItems) {
            // Set Mapped Items
            const newMappedItems = mappedItems.concat(unmappedSelectedItems);
            props.sortMappingItems(newMappedItems);
            setMappedItems(newMappedItems);

            // Set unmapped items
            const newUnmappedItems = difference(unmappedItems, unmappedSelectedItems);
            props.sortMappingItems(newUnmappedItems);
            setUnmappedItems(newUnmappedItems);

            // Update function with currently mapped items
            setSelected(difference(selected, unmappedSelectedItems));
            props.handleChange(newMappedItems.map((item) => item.id));
        }
    };

    const handleMappedToUnmapped = () => {
        if (unmappedItems && mappedItems) {
            // Set unmapped items
            const newUnmappedItems = unmappedItems.concat(mappedSelectedItems);
            props.sortMappingItems(newUnmappedItems);
            setUnmappedItems(newUnmappedItems);

            // Set Mapped items
            const newMappedItems = difference(mappedItems, mappedSelectedItems);
            props.sortMappingItems(newMappedItems);
            setMappedItems(newMappedItems);

            // Update function with currently mapped items
            setSelected(difference(selected, mappedSelectedItems));
            props.handleChange(newMappedItems.map((item) => item.id));
        }
    };

    const renderItemsList = (items: T[]) => (
        <div className={styles.list}>
            <div className={styles.scrollContainer}>{props.renderMappingItems(items, handleSelected, selected)}</div>
        </div>
    );

    const filteredMappedItemCount = () => {
        const amountNotFiltered = unmappedItems.filter((item) => {
            return item.title.toLowerCase().includes(filterValue.toLowerCase()) || item.description.toLowerCase().includes(filterValue.toLowerCase());
        }).length;

        return `${unmappedItems.length - amountNotFiltered} item(s) hidden by the search filter`;
    };

    const filteredUnmappedItemCount = () => {
        const amountNotFiltered = mappedItems.filter((item) => {
            return item.title.toLowerCase().includes(filterValue.toLowerCase()) || item.description.toLowerCase().includes(filterValue.toLowerCase());
        }).length;

        return `${mappedItems.length - amountNotFiltered} item(s) hidden by the search filter`;
    };

    return (
        <>
            <div className={styles.search}>
                <div className={styles.searchButton}>
                    <FormFieldText formFieldId="filterValue" handleChange={setFilter} formFieldLabel="Search for Item" value={filterValue} />
                </div>
                {filterValue && (
                    <Button variant="primary" onClick={() => setFilterValue('')} fontAwesomeImage={ICON_CLOSE}>
                        CLEAR
                    </Button>
                )}
            </div>
            <div className={styles.grids}>
                <div className={styles.listContainer} data-testid="unselected items">
                    <div className={styles.listHeading}>
                        <Text variant="Header3">Available</Text>
                        {filterValue && (
                            <div className={styles.listSubHeader}>
                                <Text variant="Header4">{filteredMappedItemCount()}</Text>
                            </div>
                        )}
                    </div>
                    {renderItemsList(
                        unmappedItems.filter((item) => {
                            return item.title.toLowerCase().includes(filterValue.toLowerCase()) || item.description.toLowerCase().includes(filterValue.toLowerCase());
                        })
                    )}
                </div>
                <div className={styles.buttonContainer}>
                    <div className={styles.buttonTop}>
                        <IconButton aria-label="Select Item to be Mapped" onClick={handleUnmappedToMapped} fontAwesomeImage={ICON_MOVE_OR_SCROLL_RIGHT} disabled={false} />
                    </div>
                    <IconButton aria-label="Select Item to No Longer Be Mapped" onClick={handleMappedToUnmapped} fontAwesomeImage={ICON_MOVE_OR_SCROLL_LEFT} disabled={false} />
                </div>
                <div className={styles.listContainer} data-testid="selected items">
                    <div className={styles.listHeading}>
                        <Text variant="Header3">Selected</Text>
                        {filterValue && (
                            <div className={styles.listSubHeader}>
                                <Text variant="Header4">{filteredUnmappedItemCount()}</Text>
                            </div>
                        )}
                    </div>
                    {renderItemsList(
                        mappedItems.filter((item) => {
                            return item.title.toLowerCase().includes(filterValue.toLowerCase()) || item.description.toLowerCase().includes(filterValue.toLowerCase());
                        })
                    )}
                </div>
            </div>
        </>
    );
};
